import { MusicalNoteIcon } from "@heroicons/react/24/outline";
import {
  BackwardIcon,
  ForwardIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from "@heroicons/react/24/solid";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAudio } from "../contexts/AudioContext";
import { AudioChapter } from "../types/models";

// Extend the Window type to include audioRefs
declare global {
  interface Window {
    audioRefs: React.RefObject<HTMLAudioElement>[];
  }
}

type Props = {
  playlist: AudioChapter[];
  title?: string;
};

const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const AudioTrackItem: FC<{
  track: AudioChapter;
  index: number;
  isActive: boolean;
  onPlay: () => void;
}> = ({ track, index, isActive, onPlay }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [duration, setDuration] = useState<number>(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  // Exposer la ref à l'AudioContext
  useEffect(() => {
    if (audioRef.current) {
      window.audioRefs = window.audioRefs || [];
      window.audioRefs[index] = audioRef;
    }
  }, [index]);

  return (
    <div
      className={`flex items-center p-3 rounded-md transition-all duration-200 ${
        isActive
          ? "bg-delicate-100 border-l-4 border-delicate-600"
          : "hover:bg-delicate-10"
      }`}
    >
      <button
        onClick={onPlay}
        className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 transition-colors ${
          isActive
            ? "bg-delicate-600 text-white"
            : "bg-delicate-200 text-delicate-600 hover:bg-delicate-300"
        }`}
      >
        <PlayIcon className="w-4 h-4" />
      </button>

      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium truncate ${
            isActive ? "text-delicate-800" : "text-slate-800"
          }`}
        >
          {track.title}
        </p>
        <p className="text-xs text-slate-500">{formatTime(duration)}</p>
      </div>

      <audio ref={audioRef} preload="metadata">
        <source src={track.url} type="audio/mpeg" />
      </audio>
    </div>
  );
};

const TransportControls: FC<{
  currentTrack: AudioChapter | null;
  canPlayPrevious: boolean;
  canPlayNext: boolean;
  onPlayPrevious: () => void;
  onPlayNext: () => void;
}> = ({
  currentTrack,
  canPlayPrevious,
  canPlayNext,
  onPlayPrevious,
  onPlayNext,
}) => {
  const {
    currentTime,
    duration,
    isPlaying,
    volume,
    isMuted,
    playAudio,
    pauseAudio,
    seekTo,
    setVolume,
    toggleMute,
    currentTrackIndex,
    currentPlayingRef,
  } = useAudio();

  // Utiliser la durée du titre actuel si elle est disponible
  const [localDuration, setLocalDuration] = useState(0);

  useEffect(() => {
    if (currentPlayingRef?.current) {
      const audio = currentPlayingRef.current;
      const handleLoadedMetadata = () => {
        setLocalDuration(audio.duration);
      };

      if (audio.duration) {
        setLocalDuration(audio.duration);
      }

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () =>
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    }
  }, [currentPlayingRef]);

  const actualDuration = localDuration || duration;

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clickPosition = parseFloat(e.target.value);
    const newTime = (clickPosition / 100) * actualDuration;
    seekTo(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value) / 100;
    setVolume(newVolume);
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
    } else if (currentPlayingRef?.current) {
      // Reprendre la lecture du titre actuel
      currentPlayingRef.current.play();
    } else if (currentTrack && currentTrackIndex !== null) {
      // Si aucun titre n'était en cours, jouer le titre sélectionné
      const audioRefs = window.audioRefs || [];
      const audioRef = audioRefs[currentTrackIndex];
      if (audioRef) {
        playAudio(audioRef, currentTrackIndex);
      }
    }
  };

  const progress =
    actualDuration > 0 ? (currentTime / actualDuration) * 100 : 0;

  return (
    <div className="bg-white border-t p-4 shadow-sm">
      {/* Progress Bar */}
      <div className="mb-4">
        <input
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={handleProgressChange}
          className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, #5c93b0 0%, #5c93b0 ${progress}%, #e2e8f0 ${progress}%, #e2e8f0 100%)`,
          }}
        />
      </div>

      {/* Transport Controls */}
      <div className="flex items-center justify-center sm:justify-between">
        {/* Current Track Info - Section gauche */}
        <div className="hidden sm:flex items-center flex-1">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">
              {currentTrack && currentTrack.title}
            </p>
            <p className="text-xs text-slate-500">
              {formatTime(currentTime)} / {formatTime(actualDuration)}
            </p>
          </div>
        </div>

        {/* Boutons de navigation - Section centrale */}
        <div className="flex items-center space-x-6">
          {/* Previous */}
          <button
            onClick={onPlayPrevious}
            disabled={!canPlayPrevious}
            className={`p-2 rounded-full transition-colors ${
              canPlayPrevious
                ? "hover:bg-delicate-100 text-slate-600"
                : "text-slate-300 cursor-not-allowed"
            }`}
            title="Piste précédente"
          >
            <BackwardIcon className="w-6 h-6" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="p-4 rounded-full bg-delicate-600 hover:bg-delicate-700 transition-colors"
            title={isPlaying ? "Pause" : "Lecture"}
          >
            {isPlaying ? (
              <PauseIcon className="w-6 h-6 text-white" />
            ) : (
              <PlayIcon className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Next */}
          <button
            onClick={onPlayNext}
            disabled={!canPlayNext}
            className={`p-2 rounded-full transition-colors ${
              canPlayNext
                ? "hover:bg-delicate-100 text-slate-600"
                : "text-slate-300 cursor-not-allowed"
            }`}
            title="Piste suivante"
          >
            <ForwardIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Volume Controls - Section droite */}
        <div className="hidden sm:flex items-center space-x-2 flex-1 justify-end">
          <button
            onClick={toggleMute}
            className="p-1 rounded-full hover:bg-delicate-100 transition-colors"
            title={isMuted ? "Activer le son" : "Couper le son"}
          >
            {isMuted ? (
              <SpeakerXMarkIcon className="w-5 h-5 text-slate-600" />
            ) : (
              <SpeakerWaveIcon className="w-5 h-5 text-slate-600" />
            )}
          </button>

          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume * 100}
            onChange={handleVolumeChange}
            className="w-20 h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #5c93b0 0%, #5c93b0 ${
                isMuted ? 0 : volume * 100
              }%, #e2e8f0 ${isMuted ? 0 : volume * 100}%, #e2e8f0 100%)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const AudioPlaylistCard: FC<Props> = ({ playlist, title }) => {
  const { t } = useTranslation();
  const { currentTrackIndex, playAudio, setPlaylist } = useAudio();

  // Mettre à jour la playlist dans le contexte
  useEffect(() => {
    const audioRefs = window.audioRefs || [];
    setPlaylist(audioRefs, playlist);
  }, [playlist, setPlaylist]);

  const handleTrackPlay = (index: number) => {
    const audioRefs = window.audioRefs || [];
    const audioRef = audioRefs[index];
    if (audioRef) {
      playAudio(audioRef, index);
    }
  };

  const handlePlayNext = () => {
    if (currentTrackIndex !== null && currentTrackIndex < playlist.length - 1) {
      handleTrackPlay(currentTrackIndex + 1);
    }
  };

  const handlePlayPrevious = () => {
    if (currentTrackIndex !== null && currentTrackIndex > 0) {
      handleTrackPlay(currentTrackIndex - 1);
    }
  };

  const currentTrack =
    currentTrackIndex !== null ? playlist[currentTrackIndex] : null;
  const canPlayPrevious = currentTrackIndex !== null && currentTrackIndex > 0;
  const canPlayNext =
    currentTrackIndex !== null && currentTrackIndex < playlist.length - 1;

  return (
    <div className="flex flex-col w-full p-4 sm:p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center mb-4">
        <div className="bg-delicate-50 rounded-md p-2 mr-3">
          <MusicalNoteIcon className="w-6 h-6 text-delicate-800" />
        </div>
        <p className="text-slate-700 text-xl sm:text-2xl font-bold">{title}</p>
      </div>

      <div className="w-full space-y-4">
        {/* Playlist */}
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {playlist.map((track, index) => (
            <AudioTrackItem
              key={track.chapter_id}
              track={track}
              index={index}
              isActive={currentTrackIndex === index}
              onPlay={() => handleTrackPlay(index)}
            />
          ))}
        </div>

        {/* Transport Controls */}
        <TransportControls
          currentTrack={currentTrack}
          canPlayPrevious={canPlayPrevious}
          canPlayNext={canPlayNext}
          onPlayPrevious={handlePlayPrevious}
          onPlayNext={handlePlayNext}
        />

        {playlist.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            <MusicalNoteIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>{t("dashboard.no_tracks", "Aucune piste disponible")}</p>
          </div>
        )}
      </div>

      <style>
        {`
          .slider::-webkit-slider-thumb {
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .slider::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }
        `}
      </style>
    </div>
  );
};
